'use client';

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

type Warning = { id: number; message: string; description?: string };

const WARNING_DESCRIPTIONS: Record<string, string> = {
  '이메일 또는 아이디를 입력해 주세요.': '가입할 때 등록한 이메일 주소 또는 아이디를 입력해 주세요.',
  '아이디와 비밀번호를 입력해 주세요.': '로그인에 사용하는 아이디와 비밀번호를 모두 입력해 주세요.',
  '아이디 또는 비밀번호를 입력하세요': '로그인에 사용하는 아이디와 비밀번호를 모두 입력해 주세요.',
  '비밀번호를 입력해 주세요.': '가입할 때 설정한 비밀번호를 입력해 주세요.',
  '현장을 선택해 주세요.': '로그인할 현장을 하나 선택한 뒤 확인해 주세요.',
  '이메일/아이디 또는 비밀번호가 올바르지 않습니다.': '입력한 이메일 또는 아이디와 비밀번호를 다시 확인해 주세요.',
  '계정이 올바르지 않습니다.': '입력한 이메일 또는 아이디와 비밀번호를 다시 확인해 주세요.',
  '계정 상태가 정상 사용 가능 상태가 아닙니다. 관리자에게 문의해 주세요.': '계정의 승인 또는 사용 가능 상태를 관리자에게 확인해 주세요.',
  '로그인 시도 횟수를 초과했습니다. 잠시 후 다시 시도해 주세요.': '보안을 위해 로그인이 잠시 제한되었습니다. 잠시 후 다시 시도해 주세요.',
  '로그인 서버에 연결할 수 없습니다. 잠시 후 다시 시도해 주세요.': '네트워크 상태를 확인한 뒤 잠시 후 다시 시도해 주세요.',
  '로그인 처리 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.': '입력한 정보는 유지됩니다. 잠시 후 다시 로그인해 주세요.',
  '현장이 등록되지 않은 아이디입니다. 관리자에게 문의하세요': '이 아이디에 배정된 현장이 없습니다. 현장 배정 여부를 관리자에게 문의해 주세요.',
  '요청한 현장에 접근 권한이 없습니다.': '선택한 현장에 대한 접근 권한을 관리자에게 확인해 주세요.',
  '요청한 현장 ERP ID에 접근 권한이 없습니다.': '입력한 현장 ERP ID와 해당 현장 접근 권한을 확인해 주세요.',
  '근로자 계정은 현장 ERP ID를 반드시 입력해야 합니다.': '근무 중인 현장에 부여된 ERP ID를 입력해 주세요.',
  '아이디를 입력해 주세요.': '4~50자의 영문, 숫자, 점(.), 밑줄(_), 하이픈(-)으로 입력해 주세요.',
  '아이디 중복확인을 완료해 주세요.': '아이디를 입력한 뒤 중복확인 버튼으로 사용 가능 여부를 확인해 주세요.',
  '아이디는 4자 이상 영문, 숫자, 점, 밑줄, 하이픈만 사용할 수 있습니다.': '공백, 한글 및 그 밖의 특수문자는 사용할 수 없습니다.',
  '사용할 수 없는 아이디입니다.': '이미 사용 중이거나 사용할 수 없는 아이디입니다. 다른 아이디를 입력해 주세요.',
  '중복된 아이디': '이미 등록된 아이디입니다. 다른 아이디를 입력한 뒤 중복확인을 다시 해주세요.',
  '중복된 아이디입니다.': '이미 등록된 아이디입니다. 다른 아이디를 입력한 뒤 중복확인을 다시 해주세요.',
  '아이디가 이미 사용 중입니다.': '이미 등록된 아이디입니다. 다른 아이디를 입력한 뒤 중복확인을 다시 해주세요.',
  '아이디 중복확인을 처리하지 못했습니다.': '잠시 후 중복확인을 다시 시도해 주세요. 문제가 계속되면 관리자에게 문의해 주세요.',
  '아이디 중복확인 응답을 처리하지 못했습니다.': '잠시 후 중복확인을 다시 시도해 주세요. 문제가 계속되면 관리자에게 문의해 주세요.',
  '이름을 입력해 주세요.': '실명 또는 현장에서 사용하는 이름을 입력해 주세요.',
  '비밀번호를 먼저 입력해 주세요.': '새 비밀번호를 먼저 입력한 뒤 비밀번호 확인란을 작성해 주세요.',
  '비밀번호는 대,소문자,숫자,특수문자 조합 8자리 이상이어야 합니다.': '영문 대문자, 소문자, 숫자, 특수문자를 각각 포함해 8자 이상으로 설정해 주세요.',
  '비밀번호는 대·소문자, 숫자, 특수문자를 포함해 8자리 이상 입력해 주세요.': '영문 대문자, 소문자, 숫자, 특수문자를 각각 포함해 8자 이상으로 설정해 주세요.',
  '비밀번호 확인을 입력해 주세요.': '위에 입력한 비밀번호와 같은 내용을 다시 입력해 주세요.',
  '비밀번호가 일치하지 않습니다.': '비밀번호와 비밀번호 확인란의 입력값이 같은지 확인해 주세요.',
  '이메일을 입력해 주세요.': '이메일 아이디와 도메인을 모두 입력하거나 도메인을 선택해 주세요.',
  '이메일 형식이 올바르지 않습니다.': 'example@sacon.com처럼 이메일 아이디와 도메인을 포함해 입력해 주세요.',
  '올바른 메일형식이 아닙니다.': 'example@sacon.com처럼 이메일 아이디와 도메인을 포함해 입력해 주세요.',
  '휴대폰 번호를 모두 입력해 주세요.': '휴대폰 번호의 가운데 네 자리와 마지막 네 자리를 모두 입력해 주세요.',
  '올바른 번호가 아닙니다.': '휴대폰 번호의 자릿수와 숫자만 입력했는지 확인해 주세요.',
  '현장 ERP ID를 입력해 주세요.': '소속 현장에 부여된 ERP ID를 확인해 입력해 주세요.',
  '올바른 RID가 아닙니다.': '소속 현장의 RID를 다시 확인해 입력해 주세요.',
  '필수 약관에 모두 동의해 주세요.': '이용약관, 개인정보 및 위치정보 처리 안내를 모두 확인하고 동의해 주세요.',
  '생년월일을 모두 선택해 주세요.': '생년월일의 연도, 월, 일을 모두 선택해 주세요.',
  '변경할 내용을 입력해 주세요.': '수정하려는 항목을 하나 이상 변경한 뒤 다시 시도해 주세요.',
  '회원가입 필수값을 모두 입력해 주세요.': '필수 표시된 가입 항목을 모두 입력해 주세요.',
  '이메일 또는 전화번호 중 하나를 입력해 주세요.': '이메일 주소 또는 휴대폰 번호 중 하나 이상을 입력해 주세요.',
  '회원가입을 처리하지 못했습니다.': '입력한 내용을 다시 확인한 뒤 잠시 후 가입을 시도해 주세요.',
  '회원 정보를 불러오지 못했습니다.': '네트워크 상태를 확인한 뒤 페이지를 새로고침해 주세요.',
  '회원 정보를 수정하지 못했습니다.': '입력한 내용을 확인한 뒤 다시 저장해 주세요.',
  '로그인이 필요합니다.': '로그인 상태가 만료되었을 수 있습니다. 다시 로그인해 주세요.',
  '작업자 계정만 접근할 수 있습니다.': '근로자 계정으로 로그인한 뒤 다시 시도해 주세요.',
  '회원 정보 응답을 처리하지 못했습니다.': '회원 정보 서비스 응답을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.',
  '백엔드 회원 정보 서버에 연결하지 못했습니다.': '회원 정보 서버에 연결할 수 없습니다. 잠시 후 다시 시도해 주세요.',
  '회원가입 응답을 처리하지 못했습니다.': '회원가입 서비스 응답을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.',
  '현장 목록 조회에 실패했습니다.': '접근 가능한 현장을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.',
  'BACKEND_URL 환경 변수가 설정되지 않았습니다.': '현재 인증 서비스를 사용할 수 없습니다. 관리자에게 문의해 주세요.',
};

const WARNING_DESCRIPTION_RULES: ReadonlyArray<{ pattern: RegExp; description: string }> = [
  {
    pattern: /아이디.*(?:중복|사용 중|사용할 수 없|이미 등록)/,
    description: '이미 사용 중이거나 사용할 수 없는 아이디입니다. 다른 아이디를 입력해 주세요.',
  },
  {
    pattern: /아이디.*(?:입력|형식|영문|숫자|하이픈|밑줄)/,
    description: '4~50자의 영문, 숫자, 점(.), 밑줄(_), 하이픈(-)으로 입력해 주세요.',
  },
  {
    pattern: /비밀번호.*(?:대.?소문자|특수문자|8자리|형식)/,
    description: '영문 대문자, 소문자, 숫자, 특수문자를 각각 포함해 8자 이상으로 설정해 주세요.',
  },
  {
    pattern: /비밀번호.*(?:일치|확인)/,
    description: '비밀번호와 비밀번호 확인란의 입력값이 같은지 확인해 주세요.',
  },
  {
    pattern: /이메일.*(?:형식|valid email|email address|@-sign|period)|올바른 메일.*형식/i,
    description: 'example@sacon.com처럼 이메일 아이디와 도메인을 포함해 입력해 주세요.',
  },
  {
    pattern: /이메일.*입력/,
    description: '이메일 아이디와 도메인을 모두 입력하거나 도메인을 선택해 주세요.',
  },
  {
    pattern: /(?:휴대폰|전화번호|번호).*(?:입력|형식|자리|올바르)/,
    description: '휴대폰 번호의 자릿수와 숫자만 입력했는지 확인해 주세요.',
  },
  {
    pattern: /(?:현장.*(?:ERP|RID)|(?:ERP|RID).*(?:입력|올바르|권한|확인))/i,
    description: '소속 현장에 부여된 ERP ID 또는 RID와 현장 접근 권한을 확인해 주세요.',
  },
  {
    pattern: /생년월일.*(?:선택|입력|올바르)/,
    description: '생년월일의 연도, 월, 일을 모두 선택해 주세요.',
  },
  {
    pattern: /(?:약관|개인정보|위치정보).*동의/,
    description: '필수 약관 내용을 확인하고 모두 동의해 주세요.',
  },
  {
    pattern: /(?:서버|서비스|네트워크|연결).*(?:오류|실패|불러오|처리|연결)/,
    description: '네트워크 상태를 확인한 뒤 잠시 후 다시 시도해 주세요.',
  },
];

function normalizeWarningMessage(message: string): string {
  return message.replace(/\s+/g, ' ').trim();
}

function getWarningDescription(message: string): string {
  const normalizedMessage = normalizeWarningMessage(message);
  const exactDescription = WARNING_DESCRIPTIONS[normalizedMessage];
  if (exactDescription) return exactDescription;

  const matchingRule = WARNING_DESCRIPTION_RULES.find(rule => rule.pattern.test(normalizedMessage));
  return matchingRule?.description ?? '입력한 내용과 필수 항목을 다시 확인한 뒤 다시 시도해 주세요.';
}

function getWarningTitle(message: string): string {
  return message === '이메일 또는 아이디를 입력해 주세요.' ? '이메일 또는 아이디를 입력해주세요.' : message;
}

/** 인증 화면에서 사용할 화면 상단 중앙 경고 알림입니다. */
export function useAuthWarningToast() {
  const [warning, setWarning] = useState<Warning | null>(null);
  const showWarning = useCallback((message: string, description?: string) => {
    setWarning(current => ({ id: (current?.id ?? 0) + 1, message, description }));
  }, []);
  const clearWarning = useCallback(() => setWarning(null), []);
  return { warning, showWarning, clearWarning };
}

export function AuthWarningToast({ warning, onDismiss }: { warning: Warning | null; onDismiss: () => void }) {
  const titleId = useId();
  const descriptionId = useId();
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!warning) return;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    confirmRef.current?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismiss();
      if (event.key === 'Tab') {
        event.preventDefault();
        confirmRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      previousFocus?.focus();
    };
  }, [warning, onDismiss]);

  if (!warning) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex justify-center bg-black/20 px-4 pt-[calc(40px+env(safe-area-inset-top))]"
      onMouseDown={event => {
        if (event.target === event.currentTarget) onDismiss();
      }}>
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className="flex h-fit w-full max-w-[400px] flex-col items-end gap-[26px] rounded-[3px] bg-white p-5 font-[Pretendard] shadow-[0_4px_14px_rgba(34,30,31,0.06)]">
        <div className="flex w-full flex-col gap-[6px]">
          <p id={titleId} className="m-0 w-full text-[16px] leading-normal font-semibold tracking-[-0.4px] text-[#221e1f]">
            {getWarningTitle(warning.message)}
          </p>
          <p id={descriptionId} className="m-0 w-full text-[14px] leading-normal font-normal tracking-[-0.35px] text-[#797979]">
            {warning.description ?? getWarningDescription(warning.message)}
          </p>
        </div>
        <button
          ref={confirmRef}
          type="button"
          onClick={onDismiss}
          className="flex h-[30px] w-[48px] shrink-0 cursor-pointer items-center justify-center rounded-[2px] border-0 bg-[#221e1f] text-[14px] leading-normal font-medium tracking-[-0.35px] whitespace-nowrap text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2177fa]">
          확인
        </button>
      </div>
    </div>,
    document.body
  );
}

/** 여러 오류 중 화면에서 가장 위에 있는 입력 항목의 메시지를 고릅니다. */
export function firstFieldError<Field extends string>(errors: Partial<Record<Field, string>>, order: readonly Field[]): { field: Field; message: string } | null {
  for (const field of order) {
    const message = errors[field];
    if (message) return { field, message };
  }
  return null;
}
